import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { exportTelemetryCsv } from '@/api/endpoints/telemetry';
import { ApiError, errorMessage } from '@/api/errors';

interface ExportCsvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: number;
  serialNumber: string;
}

export function ExportCsvDialog({ open, onOpenChange, deviceId, serialNumber }: ExportCsvDialogProps) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const download = useMutation({
    mutationFn: async () => {
      const blob = await exportTelemetryCsv(deviceId, {
        ...(start ? { start_time: new Date(start).toISOString() } : {}),
        ...(end ? { end_time: new Date(end).toISOString() } : {}),
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `autoaware-${serialNumber}-telemetry.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      setNotice(null);
      onOpenChange(false);
      toast.success('Export downloaded.');
    },
    onError: (error) => {
      // The API answers 404 when the window contains no rows rather than sending an
      // empty file. That is "nothing recorded then", not "device missing".
      if (error instanceof ApiError && error.isNotFound) {
        setNotice('No readings were recorded in that time range. Try a wider window.');
        return;
      }
      setNotice(errorMessage(error));
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Export telemetry"
      description="Download readings for this device as a CSV file."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={download.isPending} onClick={() => download.mutate()}>
            Download CSV
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="From"
          type="datetime-local"
          value={start}
          onChange={(event) => setStart(event.target.value)}
        />
        <TextField
          label="To"
          type="datetime-local"
          value={end}
          onChange={(event) => setEnd(event.target.value)}
          hint="Leave both blank for the most recent readings."
        />

        {notice && (
          <p className="rounded-[--radius-control] bg-warn-soft px-3 py-2 text-[13px] text-warn" role="alert">
            {notice}
          </p>
        )}
      </div>
    </Dialog>
  );
}
